// src/pages/Finance.js
import React, { useEffect, useState, useCallback } from 'react';
import { getFinanceReport, getInvoices, recordPayment, settleProfitPool, getSettlements, getSettlementById } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { T, fmt, Card, Btn, StatCard, Empty, Spinner, SectionTitle, TH, TD, INP, LBL, InfoBox, Modal } from '../components/UI';

export default function Finance() {
  const { can } = useAuth();
  const [report, setReport]     = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [payModal, setPayModal] = useState(null);
  const [payForm, setPayForm]   = useState({ amount:'', method:'MPESA', transaction_code:'' });
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');

  // Archive (settle) the current profit pool
  const [archiveModal, setArchiveModal] = useState(false);
  const [archiveNotes, setArchiveNotes] = useState('');
  const [archiving, setArchiving]       = useState(false);
  const [archiveErr, setArchiveErr]     = useState('');

  // Shares history (archived settlements)
  const [showHistory, setShowHistory]   = useState(false);
  const [settlements, setSettlements]   = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expanded, setExpanded]         = useState(null); // id of expanded settlement
  const [detail, setDetail]             = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, i] = await Promise.all([getFinanceReport(), getInvoices()]);
      setReport(r.data.data);
      setInvoices(i.data.data.filter(inv => ['VIEWED','SENT','PARTIALLY_PAID'].includes(inv.status)));
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePay = async () => {
    if (!payForm.amount || Number(payForm.amount) <= 0) return setErr('Enter a valid amount.');
    setSaving(true); setErr('');
    try {
      await recordPayment(payModal.id, payForm);
      setPayModal(null);
      setPayForm({ amount:'', method:'MPESA', transaction_code:'' });
      await load();
    } catch(e) { setErr(e.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  const handleArchive = async () => {
    setArchiving(true); setArchiveErr('');
    try {
      await settleProfitPool(archiveNotes);
      setArchiveModal(false);
      setArchiveNotes('');
      await load();
      if (showHistory) await loadHistory();
    } catch(e) { setArchiveErr(e.response?.data?.message || 'Failed to archive.'); }
    finally { setArchiving(false); }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await getSettlements();
      setSettlements(res.data.data);
    } catch(e) { console.error(e); }
    finally { setHistoryLoading(false); }
  };

  const toggleHistory = async () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && settlements.length === 0) await loadHistory();
  };

  const toggleExpand = async (id) => {
    if (expanded === id) { setExpanded(null); setDetail(null); return; }
    setExpanded(id); setDetail(null); setDetailLoading(true);
    try {
      const res = await getSettlementById(id);
      setDetail(res.data.data);
    } catch(e) { console.error(e); }
    finally { setDetailLoading(false); }
  };

  if (loading) return <Spinner />;
  const t        = report?.totals   || {};
  const payments = report?.payments || [];
  const expenses = report?.expenses || [];

  // Helper — per-payment split shown in the table uses per-payment fund %
  const payFund   = (p) => parseFloat(p.amount) * (parseFloat(p.business_fund_percent)/100);
  const payProfit = (p) => parseFloat(p.amount) * (1 - parseFloat(p.business_fund_percent)/100);

  return (
    <div>
      <SectionTitle sub="Managed by Finance · printing cost deducted first, then fund/pool split, then expenses off Business Fund">Finance & Accounts</SectionTitle>
      {!can('confirm_payment') && <InfoBox>ℹ Payment confirmation is handled by Finance.</InfoBox>}

      {/* Awaiting confirmation queue */}
      {can('confirm_payment') && invoices.length > 0 && (
        <div style={{ background:`${T.warning}11`, border:`1px solid ${T.warning}44`, borderRadius:'10px', padding:'18px', marginBottom:'20px' }}>
          <div style={{ fontWeight:700, color:T.warning, marginBottom:'12px' }}>⏳ {invoices.length} Payment{invoices.length>1?'s':''} Awaiting Confirmation</div>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>
              <th style={TH}>Order</th><th style={TH}>Customer</th><th style={TH}>Invoice</th><th style={TH}>Amount Due</th><th style={TH}></th>
            </tr></thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id}>
                  <td style={{ ...TD, color:T.gold, fontWeight:700 }}>{inv.order_number}</td>
                  <td style={TD}>{inv.customer_name}</td>
                  <td style={{ ...TD, color:T.info }}>{inv.invoice_number}</td>
                  <td style={{ ...TD, fontWeight:700, color:T.success }}>{fmt(inv.total_amount - inv.amount_paid)}</td>
                  <td style={TD}><Btn size="sm" onClick={() => setPayModal(inv)}>✅ Record Payment</Btn></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Payment Modal */}
      {payModal && (
        <div style={{ position:'fixed', inset:0, background:'#020812CC', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:'20px' }}>
          <div style={{ background:T.card, border:`1px solid ${T.gold}44`, borderRadius:'14px', padding:'28px', width:'100%', maxWidth:'420px' }}>
            <div style={{ fontSize:'15px', fontWeight:700, color:T.gold, marginBottom:'18px' }}>Record Payment — {payModal.order_number}</div>
            <div style={{ fontSize:'13px', color:T.muted, marginBottom:'14px' }}>
              Customer: <b style={{ color:T.text }}>{payModal.customer_name}</b> · Due: <b style={{ color:T.success }}>{fmt(payModal.total_amount - payModal.amount_paid)}</b>
            </div>
            <label style={LBL}>Amount Received (KES) *</label>
            <input style={{ ...INP, marginBottom:'12px' }} type="number" value={payForm.amount}
              onChange={e => setPayForm(f=>({...f,amount:e.target.value}))} placeholder="e.g. 15000" />
            <label style={LBL}>Payment Method</label>
            <select value={payForm.method} onChange={e => setPayForm(f=>({...f,method:e.target.value}))}
              style={{ ...INP, marginBottom:'12px' }}>
              <option>MPESA</option><option>BANK_TRANSFER</option><option>CASH</option><option>CHEQUE</option>
            </select>
            <label style={LBL}>Transaction Code / Reference</label>
            <input style={{ ...INP, marginBottom:'16px' }} value={payForm.transaction_code}
              onChange={e => setPayForm(f=>({...f,transaction_code:e.target.value}))} placeholder="e.g. QHX4R8..." />
            {err && <div style={{ color:T.danger, fontSize:'12px', marginBottom:'10px' }}>{err}</div>}
            <div style={{ display:'flex', gap:'10px' }}>
              <Btn onClick={handlePay} disabled={saving} style={{ flex:1 }}>{saving?'Recording...':'Confirm Payment'}</Btn>
              <Btn variant="outline" onClick={() => { setPayModal(null); setErr(''); }} style={{ flex:1 }}>Cancel</Btn>
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats — uses correct key names from backend */}
      <div style={{ display:'flex', gap:'14px', flexWrap:'wrap', marginBottom:'20px' }}>
        <StatCard label="Total Collected"   value={fmt(t.totalCollected)}   color={T.gold} />
        <StatCard label="Printing Cost (deducted)" value={fmt(t.totalPrinting)} color={T.info} sub="Off the top before split" />
        <StatCard label={`Business Fund (${t.avgFundPercent||50}%)`} value={fmt(t.grossBusinessFund)} color={T.success} />
        <StatCard label="Profit Pool"       value={fmt(t.grossProfitPool)}  color={T.warning} />
      </div>

      <div style={{ display:'flex', gap:'14px', flexWrap:'wrap', marginBottom:'24px' }}>
        <StatCard label="Expenses (off Business Fund)" value={fmt(t.totalExpenses)} color={T.danger} sub="After split" />
        <StatCard label="Net Business Fund" value={fmt(t.netBusinessFund)}  color={T.success} sub="After expenses" />
        <StatCard label="Net Profit Pool"   value={fmt(t.netProfitPool)}    color={T.warning} sub="Unaffected by expenses" />
        <StatCard label={`Per Member (÷${t.profitPoolMembers||5})`} value={fmt(t.perMember)} color={T.gold} />
      </div>

      {/* Flow explanation */}
      <Card style={{ marginBottom:'20px', padding:'16px 20px' }}>
        <div style={{ fontSize:'12px', color:T.muted, fontWeight:600, marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.5px' }}>How the split works</div>
        <div style={{ display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap', fontSize:'13px' }}>
          <span style={{ background:`${T.gold}22`, color:T.gold, padding:'5px 12px', borderRadius:'20px', fontWeight:700 }}>
            KES {(t.totalCollected||0).toLocaleString()} collected
          </span>
          <span style={{ color:T.muted }}>−</span>
          <span style={{ background:`${T.info}22`, color:T.info, padding:'5px 12px', borderRadius:'20px', fontWeight:700 }}>
            KES {(t.totalPrinting||0).toLocaleString()} printing
          </span>
          <span style={{ color:T.muted }}>=</span>
          <span style={{ background:`${T.success}22`, color:T.success, padding:'5px 12px', borderRadius:'20px', fontWeight:700 }}>
            KES {(t.grossBusinessFund||0).toLocaleString()} Business Fund
          </span>
          <span style={{ color:T.muted }}>+</span>
          <span style={{ background:`${T.warning}22`, color:T.warning, padding:'5px 12px', borderRadius:'20px', fontWeight:700 }}>
            KES {(t.grossProfitPool||0).toLocaleString()} Profit Pool
          </span>
          <span style={{ color:T.muted }}>then</span>
          <span style={{ background:`${T.danger}22`, color:T.danger, padding:'5px 12px', borderRadius:'20px', fontWeight:700 }}>
            − KES {(t.totalExpenses||0).toLocaleString()} expenses
          </span>
          <span style={{ color:T.muted }}>from Business Fund only</span>
        </div>
      </Card>

      {/* Member distribution */}
      <Card style={{ marginBottom:'20px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'14px', flexWrap:'wrap', gap:'10px' }}>
          <div style={{ fontSize:'13px', fontWeight:700, color:T.gold }}>👥 Profit Pool — Member Shares</div>
          {can('settle_profit_pool') && (t.netProfitPool||0) > 0 && (
            <Btn size="sm" variant="outline" onClick={() => setArchiveModal(true)}>📦 Archive / Settle Pool</Btn>
          )}
        </div>
        {(t.netProfitPool||0) > 0 ? (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><th style={TH}>Member</th><th style={TH}>Share</th></tr></thead>
            <tbody>
              {Array.from({ length: t.profitPoolMembers || 5 }, (_, i) => (
                <tr key={i}>
                  <td style={{ ...TD, color:T.text }}>Member {i+1}</td>
                  <td style={{ ...TD, color:T.gold, fontWeight:700 }}>{fmt(t.perMember)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <Empty msg="No payments collected yet." />}
      </Card>

      {/* Archive confirmation modal */}
      {archiveModal && (
        <Modal title="Archive / Settle Profit Pool" onClose={() => !archiving && setArchiveModal(false)}>
          <InfoBox variant="warning">
            This locks in the current pool as paid out and resets the live dashboard to zero.
            Only do this once shares have actually been handed to members — the record moves
            to Shares History and can't be edited afterwards.
          </InfoBox>
          <div style={{ display:'flex', gap:'14px', flexWrap:'wrap', marginBottom:'16px' }}>
            <StatCard label="Net Profit Pool" value={fmt(t.netProfitPool)} color={T.warning} />
            <StatCard label={`Per Member (÷${t.profitPoolMembers||5})`} value={fmt(t.perMember)} color={T.gold} />
          </div>
          <label style={LBL}>Notes (optional)</label>
          <textarea style={{ ...INP, marginBottom:'14px', minHeight:'70px', resize:'vertical' }}
            value={archiveNotes} onChange={e => setArchiveNotes(e.target.value)}
            placeholder="e.g. Paid out in cash at the Friday meeting" />
          {archiveErr && <div style={{ color:T.danger, fontSize:'12px', marginBottom:'10px' }}>{archiveErr}</div>}
          <div style={{ display:'flex', gap:'10px' }}>
            <Btn onClick={handleArchive} disabled={archiving} style={{ flex:1 }}>{archiving ? 'Archiving...' : '✅ Confirm — Shares Settled'}</Btn>
            <Btn variant="outline" onClick={() => setArchiveModal(false)} disabled={archiving} style={{ flex:1 }}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {/* Payment records */}
      <Card style={{ marginBottom:'20px' }}>
        <div style={{ fontSize:'13px', fontWeight:700, color:T.gold, marginBottom:'14px' }}>✅ Payment Records</div>
        {payments.length === 0 ? <Empty msg="No payments recorded yet." /> : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                <th style={TH}>Order</th><th style={TH}>Customer</th><th style={TH}>Amount</th>
                <th style={TH}>Method</th><th style={TH}>Ref</th>
                <th style={TH}>Business Fund</th><th style={TH}>Profit Pool</th>
              </tr></thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id}>
                    <td style={{ ...TD, color:T.gold, fontWeight:700 }}>{p.order_number}</td>
                    <td style={TD}>{p.customer_name}</td>
                    <td style={{ ...TD, fontWeight:700, color:T.success }}>{fmt(p.amount)}</td>
                    <td style={{ ...TD, fontSize:'12px' }}>{p.method}</td>
                    <td style={{ ...TD, fontSize:'11px', color:T.muted }}>{p.transaction_code||'—'}</td>
                    <td style={{ ...TD, color:T.success }}>{fmt(payFund(p))}</td>
                    <td style={{ ...TD, color:T.warning }}>{fmt(payProfit(p))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Expenses */}
      <Card>
        <div style={{ fontSize:'13px', fontWeight:700, color:T.danger, marginBottom:'14px' }}>💸 Expenses (deduct from Business Fund)</div>
        {expenses.length === 0 ? <Empty msg="No expenses recorded yet." /> : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                <th style={TH}>Date</th><th style={TH}>Category</th><th style={TH}>Description</th>
                <th style={TH}>Linked Order</th><th style={TH}>By</th><th style={TH}>Amount</th>
              </tr></thead>
              <tbody>
                {expenses.map(e => (
                  <tr key={e.id}>
                    <td style={TD}>{new Date(e.created_at).toLocaleDateString('en-KE')}</td>
                    <td style={TD}><span style={{ fontSize:'11px', background:`${T.border}`, borderRadius:'5px', padding:'3px 8px' }}>{e.category}</span></td>
                    <td style={TD}>{e.description||'—'}</td>
                    <td style={{ ...TD, color:T.gold, fontSize:'12px' }}>{e.order_number||'—'}</td>
                    <td style={{ ...TD, fontSize:'12px', color:T.muted }}>{e.recorded_by_name}</td>
                    <td style={{ ...TD, fontWeight:700, color:T.danger }}>−{fmt(e.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Shares history — archived settlements */}
      <Card style={{ marginTop:'20px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }}
          onClick={toggleHistory}>
          <div style={{ fontSize:'13px', fontWeight:700, color:T.gold }}>🗄 Shares History (Archived Settlements)</div>
          <span style={{ color:T.muted, fontSize:'12px' }}>{showHistory ? '▲ Hide' : '▼ Show'}</span>
        </div>

        {showHistory && (
          historyLoading ? <div style={{ marginTop:'16px' }}><Spinner /></div> :
          settlements.length === 0 ? <div style={{ marginTop:'16px' }}><Empty msg="No settlements archived yet." /></div> : (
            <div style={{ marginTop:'16px' }}>
              {settlements.map(s => (
                <div key={s.id} style={{ border:`1px solid ${T.border}`, borderRadius:'10px', marginBottom:'12px', overflow:'hidden' }}>
                  <div onClick={() => toggleExpand(s.id)}
                    style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', cursor:'pointer', flexWrap:'wrap', gap:'8px' }}>
                    <div>
                      <div style={{ fontWeight:700, color:T.text, fontSize:'13px' }}>
                        Settlement #{s.id} — {new Date(s.created_at).toLocaleDateString('en-KE', { year:'numeric', month:'short', day:'numeric' })}
                      </div>
                      <div style={{ fontSize:'11px', color:T.muted, marginTop:'2px' }}>
                        Archived by {s.settled_by_name} · {s.payment_count} payment{s.payment_count!==1?'s':''} · {s.expense_count} expense{s.expense_count!==1?'s':''}
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:'18px', alignItems:'center' }}>
                      <div style={{ textAlign:'right' }}>
                        <div style={{ fontSize:'11px', color:T.muted }}>Per Member (÷{s.profit_pool_members})</div>
                        <div style={{ fontWeight:700, color:T.gold }}>{fmt(s.per_member)}</div>
                      </div>
                      <span style={{ color:T.muted }}>{expanded === s.id ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {expanded === s.id && (
                    <div style={{ padding:'0 16px 16px', borderTop:`1px solid ${T.surface}` }}>
                      <div style={{ display:'flex', gap:'14px', flexWrap:'wrap', margin:'14px 0' }}>
                        <StatCard label="Collected" value={fmt(s.total_collected)} color={T.gold} />
                        <StatCard label="Printing" value={fmt(s.total_printing)} color={T.info} />
                        <StatCard label="Net Business Fund" value={fmt(s.net_business_fund)} color={T.success} />
                        <StatCard label="Net Profit Pool" value={fmt(s.net_profit_pool)} color={T.warning} />
                      </div>
                      {s.notes && <div style={{ fontSize:'12px', color:T.muted, marginBottom:'12px' }}>📝 {s.notes}</div>}

                      <div style={{ fontSize:'12px', fontWeight:700, color:T.gold, marginBottom:'8px' }}>Member Shares</div>
                      <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:'14px' }}>
                        <thead><tr><th style={TH}>Member</th><th style={TH}>Share</th></tr></thead>
                        <tbody>
                          {Array.from({ length: s.profit_pool_members || 5 }, (_, i) => (
                            <tr key={i}>
                              <td style={{ ...TD, color:T.text }}>Member {i+1}</td>
                              <td style={{ ...TD, color:T.gold, fontWeight:700 }}>{fmt(s.per_member)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {detailLoading ? <Spinner /> : detail && detail.settlement.id === s.id && (
                        <>
                          <div style={{ fontSize:'12px', fontWeight:700, color:T.gold, marginBottom:'8px' }}>Payments Included</div>
                          {detail.payments.length === 0 ? <Empty msg="None." /> : (
                            <div style={{ overflowX:'auto', marginBottom:'14px' }}>
                              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                                <thead><tr><th style={TH}>Order</th><th style={TH}>Customer</th><th style={TH}>Amount</th><th style={TH}>Date</th></tr></thead>
                                <tbody>
                                  {detail.payments.map(p => (
                                    <tr key={p.id}>
                                      <td style={{ ...TD, color:T.gold }}>{p.order_number}</td>
                                      <td style={TD}>{p.customer_name}</td>
                                      <td style={{ ...TD, color:T.success, fontWeight:700 }}>{fmt(p.amount)}</td>
                                      <td style={{ ...TD, fontSize:'12px', color:T.muted }}>{new Date(p.created_at).toLocaleDateString('en-KE')}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          {detail.expenses.length > 0 && (
                            <>
                              <div style={{ fontSize:'12px', fontWeight:700, color:T.danger, marginBottom:'8px' }}>Expenses Included</div>
                              <div style={{ overflowX:'auto' }}>
                                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                                  <thead><tr><th style={TH}>Category</th><th style={TH}>Description</th><th style={TH}>Amount</th></tr></thead>
                                  <tbody>
                                    {detail.expenses.map(e => (
                                      <tr key={e.id}>
                                        <td style={TD}>{e.category}</td>
                                        <td style={TD}>{e.description||'—'}</td>
                                        <td style={{ ...TD, color:T.danger, fontWeight:700 }}>−{fmt(e.amount)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </Card>
    </div>
  );
}
